import { type AxiosInstance, HttpStatusCode, isAxiosError } from "axios";
import { type FormData as MapSearchFormDataType } from "../components/MapSearchDialog";
import { SendToTrackingService } from "@/api/handlers/errors";

const TIMEOUT_MILLISECOND = 60_000;

interface GetMapRealEstateSearchAPIResponse {
  data?: GetMapRealEstateSearchAPIResponseBody;
  status: number;
  error?: string;
  timeout?: boolean;
}

interface GetMapRealEstateSearchAPIResponseBody {
  count?: number;
  list?: Array<{
    id: number;
    landId: number;
    buildingId: number;
    buildingName: string;
    hotelName: string | null;
    dormitoryName: string | null;
    industrialFacilityName: string | null;
    dormitoryKind: string | null;
    parkingLotName: string | null;
    realEstateType: string;
    prefName: string;
    cityName: string;
    location: string;
    chibanKaokuNumber: string;
    address: string;
    area: number | null;
    latitude: number;
    longitude: number;
  }>;
}

export const getMapRealEstateSearchAPI = async (
  apiClient: AxiosInstance,
  data: MapSearchFormDataType
): Promise<GetMapRealEstateSearchAPIResponse | undefined> => {
  // タイムアウトの実装
  // タイムアウトした場合、AbortController を使ってリクエスト自体をキャンセルする
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, TIMEOUT_MILLISECOND);

  try {
    const response =
      await apiClient.post<GetMapRealEstateSearchAPIResponseBody>(
        "map/real-estate-search",
        // 土地の住居表示は検索時に取らず、ピンを開いた時に1件ずつ取る（getRealEstateSearchAddressAPI）。
        // 検索時に全件取ると Google の逆ジオコーディングの回数制限に当たるため。建物には影響しない
        { ...data, includeLandAddress: false },
        { signal: controller.signal }
      );

    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    if (isAxiosError(error) && error.code === "ERR_CANCELED") {
      return {
        status: HttpStatusCode.Ok,
        timeout: true,
      };
    } else if (isAxiosError<GetMapRealEstateSearchAPIResponse>(error)) {
      if (
        (error.response?.status ?? HttpStatusCode.InternalServerError) >=
        HttpStatusCode.InternalServerError
      ) {
        SendToTrackingService(error);
      }

      // Unauthorized以外のエラーが発生した場合、ステータスコードを返す
      if (error.response?.status !== HttpStatusCode.Unauthorized) {
        return {
          status: error.response?.status ?? HttpStatusCode.InternalServerError, // ステータスコードが取得できない場合は500を返す
          error: error.response?.data?.error,
        };
      }
    }

    // その他のエラーの場合はundefinedを返す
    return undefined;
  } finally {
    // 処理終了後は不要なタイマーを片付ける
    clearTimeout(timeoutId);
  }
};
